import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type CSSProperties } from "react";
const ITEM_STAGGER_DELAY_MS = 120;
const ITEM_STAGGER_OBSERVER_OPTIONS: IntersectionObserverInit = {
  rootMargin: "120px 0px",
  threshold: 0.01
};
const ITEM_STAGGER_CLASS_NAME = "h-full min-w-0 motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]";
const ITEM_STAGGER_VISIBLE_CLASS_NAME = "motion-safe:translate-y-0 motion-safe:opacity-100";
const ITEM_STAGGER_HIDDEN_CLASS_NAME = "motion-safe:translate-y-3 motion-safe:opacity-0";
type VisibleItemOrder = Record<string, number>;
const staggeredItemKeys = new Set<string>();
type ItemStaggerHookOptions = {
  continueFromVisibleItems?: boolean;
  groupKey?: string;
  refreshKey?: string;
  triggerOnMount?: boolean;
};
type ItemStaggerPropertiesOptions = {
  className?: string;
};
export type ItemStaggerPropertiesGetter = (itemKey: string, index: number, options?: ItemStaggerPropertiesOptions) => {
  className: string;
  "data-listing-item-index": number;
  "data-listing-item-key": string;
  ref: (element: HTMLElement | null) => void;
  style: CSSProperties;
};
function getItemAnimationDelay(order: number) {
  return `${order * ITEM_STAGGER_DELAY_MS}ms`;
}
function getItemClassName(isVisible: boolean, className = "") {
  return `${ITEM_STAGGER_CLASS_NAME} ${isVisible ? ITEM_STAGGER_VISIBLE_CLASS_NAME : ITEM_STAGGER_HIDDEN_CLASS_NAME} ${className}`;
}
function getVisibleItemOrder(itemKeys: Array<string>) {
  return Object.fromEntries(itemKeys.map(itemKey => [itemKey, 0]));
}
function getStaggeredItemKey(groupKey: string, itemKey: string) {
  return `${groupKey}:${itemKey}`;
}
function getItemStaggerHookOptions(groupKeyOrOptions?: string | ItemStaggerHookOptions) {
  if (typeof groupKeyOrOptions === "string") {
    return {
      groupKey: groupKeyOrOptions
    };
  }
  return groupKeyOrOptions || {};
}
function getStaggeredItemOrder(itemKeys: Array<string>, groupKey?: string) {
  if (!groupKey) return {};
  return Object.fromEntries(itemKeys.filter(itemKey => staggeredItemKeys.has(getStaggeredItemKey(groupKey, itemKey))).map(itemKey => [itemKey, 0]));
}
function keepVisibleItemsInList(current: VisibleItemOrder, itemKeySet: Set<string>) {
  const next: VisibleItemOrder = {};
  let hasChanged = false;
  for (const [itemKey, order] of Object.entries(current)) {
    if (itemKeySet.has(itemKey)) {
      next[itemKey] = order;
      continue;
    }
    hasChanged = true;
  }
  return hasChanged ? next : current;
}
function getNextVisibleItemOrder(current: VisibleItemOrder) {
  const currentOrders = Object.values(current);
  if (currentOrders.length === 0) return 0;
  return Math.max(...currentOrders) + 1;
}
function revealItemsInOrder(current: VisibleItemOrder, itemKeys: Array<string>, continueFromVisibleItems: boolean, groupKey?: string) {
  const next = {
    ...current
  };
  let order = continueFromVisibleItems ? getNextVisibleItemOrder(current) : 0;
  let hasChanged = false;
  for (const itemKey of itemKeys) {
    if (next[itemKey] !== undefined) continue;
    next[itemKey] = order;
    if (groupKey) staggeredItemKeys.add(getStaggeredItemKey(groupKey, itemKey));
    order += 1;
    hasChanged = true;
  }
  return hasChanged ? next : current;
}
export function useItemStagger(itemKeys, groupKeyOrOptions) {
  const $ = _c(32);
  let t0;
  if ($[0] !== groupKeyOrOptions) {
    t0 = getItemStaggerHookOptions(groupKeyOrOptions);
    $[0] = groupKeyOrOptions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    continueFromVisibleItems: t1,
    groupKey,
    refreshKey: t2,
    triggerOnMount: t3
  } = t0;
  const continueFromVisibleItems = t1 === undefined ? false : t1;
  const refreshKey = t2 === undefined ? "" : t2;
  const triggerOnMount = t3 === undefined ? false : t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const itemElementsReference = useRef(t4);
  const observerReference = useRef(null);
  let t5;
  if ($[3] !== groupKey || $[4] !== itemKeys) {
    t5 = () => getStaggeredItemOrder(itemKeys, groupKey);
    $[3] = groupKey;
    $[4] = itemKeys;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [visibleItemOrder, setVisibleItemOrder] = useState(t5);
  let t6;
  if ($[6] !== itemKeys) {
    t6 = itemKeys.join("|");
    $[6] = itemKeys;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const itemKeysSignature = `${groupKey || ""}:${refreshKey}:${t6}`;
  let t7;
  if ($[8] !== groupKey || $[9] !== itemKeys) {
    t7 = () => {
      const itemKeySet = new Set(itemKeys);
      setVisibleItemOrder(current => keepVisibleItemsInList({
        ...getStaggeredItemOrder(itemKeys, groupKey),
        ...current
      }, itemKeySet));
    };
    $[8] = groupKey;
    $[9] = itemKeys;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== itemKeysSignature) {
    t8 = [itemKeysSignature];
    $[11] = itemKeysSignature;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== continueFromVisibleItems || $[14] !== groupKey || $[15] !== itemKeys || $[16] !== triggerOnMount) {
    t9 = () => {
      if (!triggerOnMount) {
        return;
      }
      setVisibleItemOrder(current_0 => revealItemsInOrder(current_0, itemKeys, continueFromVisibleItems, groupKey));
    };
    $[13] = continueFromVisibleItems;
    $[14] = groupKey;
    $[15] = itemKeys;
    $[16] = triggerOnMount;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== continueFromVisibleItems || $[19] !== groupKey || $[20] !== itemKeysSignature || $[21] !== triggerOnMount) {
    t10 = [continueFromVisibleItems, groupKey, itemKeysSignature, triggerOnMount];
    $[18] = continueFromVisibleItems;
    $[19] = groupKey;
    $[20] = itemKeysSignature;
    $[21] = triggerOnMount;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  if ($[23] !== continueFromVisibleItems || $[24] !== groupKey || $[25] !== itemKeys) {
    t11 = () => {
      const itemKeySet_0 = new Set(itemKeys);
      if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        setVisibleItemOrder(getVisibleItemOrder(itemKeys));
        return;
      }
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setVisibleItemOrder(getVisibleItemOrder(itemKeys));
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const enteringEntries = entries.filter(_temp).sort(_temp2);
        if (enteringEntries.length === 0) {
          return;
        }
        setVisibleItemOrder(current_1 => {
          const next = {
            ...current_1
          };
          let order = continueFromVisibleItems ? getNextVisibleItemOrder(current_1) : 0;
          let hasChanged = false;
          for (const entry_0 of enteringEntries) {
            const itemKey = (entry_0.target as HTMLElement).dataset.listingItemKey;
            if (!itemKey || !itemKeySet_0.has(itemKey) || next[itemKey] !== undefined) {
              continue;
            }
            next[itemKey] = order;
            if (groupKey) {
              staggeredItemKeys.add(getStaggeredItemKey(groupKey, itemKey));
            }
            order = order + 1;
            hasChanged = true;
          }
          return hasChanged ? next : current_1;
        });
        for (const entry_1 of enteringEntries) {
          observer.unobserve(entry_1.target);
        }
      }, ITEM_STAGGER_OBSERVER_OPTIONS);
      observerReference.current = observer;
      for (const [itemKey_0, element] of itemElementsReference.current) {
        if (itemKeySet_0.has(itemKey_0)) {
          observer.observe(element);
        }
      }
      return () => {
        observer.disconnect();
        if (observerReference.current === observer) {
          observerReference.current = null;
        }
      };
    };
    $[23] = continueFromVisibleItems;
    $[24] = groupKey;
    $[25] = itemKeys;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== continueFromVisibleItems || $[28] !== itemKeysSignature) {
    t12 = [continueFromVisibleItems, itemKeysSignature];
    $[27] = continueFromVisibleItems;
    $[28] = itemKeysSignature;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  if ($[30] !== visibleItemOrder) {
    const getItemStaggerProperties = (itemKey_1, index, options) => {
      const animationOrder = visibleItemOrder[itemKey_1];
      const isVisible = animationOrder !== undefined;
      return {
        className: getItemClassName(isVisible, options?.className),
        "data-listing-item-index": index,
        "data-listing-item-key": itemKey_1,
        ref: element_0 => {
          if (element_0) {
            itemElementsReference.current.set(itemKey_1, element_0);
            observerReference.current?.observe(element_0);
            return;
          }
          const previousElement = itemElementsReference.current.get(itemKey_1);
          if (previousElement) {
            observerReference.current?.unobserve(previousElement);
          }
          itemElementsReference.current.delete(itemKey_1);
        },
        style: {
          transitionDelay: isVisible ? getItemAnimationDelay(animationOrder) : undefined
        }
      };
    };
    t13 = {
      getItemStaggerProperties
    };
    $[30] = visibleItemOrder;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function _temp2(first, second) {
  return Number((first.target as HTMLElement).dataset.listingItemIndex) - Number((second.target as HTMLElement).dataset.listingItemIndex);
}
function _temp(entry) {
  return entry.isIntersecting;
}